import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useProfile } from "@/hooks/useProfile";
import type { StayPlanModule } from "@/lib/daycare-hotel/stayPlans";

/** Hora local no formato `HH:mm`, que é o que `check_in_time` guarda. */
const nowHourMinute = () =>
  new Date().toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });

export interface CheckInStayDayInput {
  stayDayId: string;
  module: StayPlanModule;
  reservationId: string;
  usedOn: string;
  contractId: string | null;
  /** `plan` promove o hold reservado a débito; `avulso` não toca no plano. */
  coverage: "plan" | "avulso";
}

/**
 * Confirma a presença do pet **no dia** e debita o plano. O status vive em
 * `stay_reservation_days`; `stay_reservations.status` só diz se a reserva
 * existe ou foi cancelada.
 *
 * O débito acontece aqui, não na reserva: a reserva só segurou a unidade
 * (`source = 'hold'`). No hotel `usedOn` é ignorado no RPC (`p_used_on: null`)
 * — o check-in promove todas as noites de uma vez.
 */
export async function checkInStayDay(input: CheckInStayDayInput): Promise<void> {
  if (input.coverage === "plan" && input.contractId) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { error: planError } = await (supabase as any).rpc("promote_stay_plan_hold", {
      p_module: input.module,
      p_reservation_id: input.reservationId,
      p_used_on: input.module === "hotel" ? null : input.usedOn,
    });
    if (planError) throw planError;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { error } = await (supabase as any)
    .from("stay_reservation_days")
    .update({ status: "checked_in", check_in_time: nowHourMinute() })
    .eq("id", input.stayDayId);
  if (error) throw error;
}

export function useCheckInReservation() {
  const queryClient = useQueryClient();
  const { data: profile } = useProfile();
  const clinicId = profile?.current_clinic_id ?? null;

  return useMutation({
    mutationFn: (input: CheckInStayDayInput) => checkInStayDay(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["panel-day", clinicId] });
      queryClient.invalidateQueries({ queryKey: ["stay-schedule", clinicId] });
      queryClient.invalidateQueries({ queryKey: ["stay-plan-contracts"] });
      queryClient.invalidateQueries({ queryKey: ["patient-history-feed"] });
      queryClient.invalidateQueries({ queryKey: ["owner-history-feed"] });
    },
  });
}
